/*!
 * Copyright Optimus Ride 2021
 */

import Auth from "../../auth/abstract-auth";
import { GRPC_RESERVATION_PORT } from "../../constants";
import { ReservationServiceClient } from "../../grpc/ReservationServiceClientPb";
import { LocalDateRange, Empty } from "../../grpc/shared_pb";
import {
  AvailabilityRequest,
  BulkCancelReservationsRequest,
  CancelReservationRequest,
  CreateReservationOrErrorResponse,
  CreateReservationRequest,
  CreateReservationsRequest,
  GetReservationRequest,
  NearestAvailabilityByDayResponse,
  NearestAvailabilityRequest,
  UpdateReservationRequest,
} from "../../grpc/reservation_pb";
import { CONSOLE_ERROR_MSG, requestMetadata } from "../util";
import {
  PastReservation,
  Reservation,
  toPastReservation,
  toReservation,
} from '../../@types/grpc/reservation';
import {
  Availability,
  NearestAvailability,
  toAvailability,
  toNearestAvailability,
} from "@optimusride/client-library/@types/grpc/availability";
import { ClientReadableStream } from "grpc-web";

export default class ReservationServiceConsumer {
  private reservationServiceClient: ReservationServiceClient;
  private auth: Auth;
  private oriApiKey: undefined | string;

  constructor(auth: Auth, hostname: string, oriApiKey?: undefined | string) {
    this.auth = auth;
    this.oriApiKey = oriApiKey;
    this.reservationServiceClient = new ReservationServiceClient(`${hostname}:${GRPC_RESERVATION_PORT}`, null, null);
  }

  public getAvailability = (operatingAreaId: number, operatingAreaTimezone: string, range: LocalDateRange, seatCount?: number, reservationId?: number, now?: string, departTime?: string, userId?: number): Promise<Availability[]> => {
      const request = new AvailabilityRequest();
      request.setOperatingAreaId(operatingAreaId);
      request.setRange(range);
      if (seatCount) {
        request.setSeatCount(seatCount);
      }
      if (reservationId) {
        request.setReservationId(reservationId);
      }
      if (now) {
        request.setNow(now);
      }
      if (departTime) {
        request.setDepartTime(departTime);
      }
      if (userId) {
        request.setUserId(userId);
      }
      return new Promise(async (resolve, reject) => {
        this.reservationServiceClient.getAvailability(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
            if (!err) {
              const availability = response.getAvailability();
              if (response.hasError()) {
                reject(response.getError());
              } else if (response.hasAvailability() && availability) {
                resolve(availability.getScheduleList().map(s => toAvailability(s, operatingAreaTimezone)));
              } else {
                throw new Error(CONSOLE_ERROR_MSG);
              }
            } else {
              reject(err);
            }
          });
        });
    }

    public getNearestAvailability = (operatingAreaId: number, targetTime: string, targetDate: string[], seatCount?: number, now?: string, reservationId?: number, departTime?: string, userId?: number): Promise<NearestAvailability> => {
      const request = new NearestAvailabilityRequest();
      request.setOperatingAreaId(operatingAreaId);
      request.setTargetTime(targetTime);
      request.setTargetDateList(targetDate);
      if (seatCount) {
        request.setSeatCount(seatCount);
      }
      if (reservationId) {
        request.setReservationId(reservationId);
      }
      if (now) {
        request.setNow(now);
      }
      if (departTime) {
        request.setDepartTime(departTime);
      }
      if (userId) {
        request.setUserId(userId);
      }
      return new Promise(async (resolve, reject) => {
        this.reservationServiceClient.getNearestAvailability(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
            if (!err) {
              const availability = response.getNearestAvailability();
              if (response.hasError()) {
                reject(response.getError());
              } else if (response.hasNearestAvailability() && availability) {
                resolve(toNearestAvailability(availability));
              } else {
                throw new Error(CONSOLE_ERROR_MSG);
              }
            } else {
              reject(err);
            }
          });
        });
    }

    public getNearestAvailabilityByDay = (operatingAreaId: number, targetTime: string, targetDate: string[], seatCount?: number, now?: string, reservationId?: number, departTime?: string, userId?: number): Promise<NearestAvailabilityByDayResponse> => {
      const request = new NearestAvailabilityRequest();
      request.setOperatingAreaId(operatingAreaId);
      request.setTargetTime(targetTime);
      request.setTargetDateList(targetDate);
      if (seatCount) {
        request.setSeatCount(seatCount);
      }
      if (reservationId) {
        request.setReservationId(reservationId);
      }
      if (now) {
        request.setNow(now);
      }
      if (departTime) {
        request.setDepartTime(departTime);
      }
      if (userId) {
        request.setUserId(userId);
      }
      return new Promise(async (resolve, reject) => {
        this.reservationServiceClient.getNearestAvailabilityByDay(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
            if (!err) {
              const availability = response.getNearestAvailability();
              if (response.hasError()) {
                reject(response.getError());
              } else if (response.hasNearestAvailability() && availability) {
                resolve(availability);
              } else {
                throw new Error(CONSOLE_ERROR_MSG);
              }
            } else {
              reject(err);
            }
          });
        });
    }

    public getReservations = (operatingAreaId: number, operatingAreaTimezone: string, userId?: number): Promise<Reservation[]> => {
      const request = new GetReservationRequest();
      request.setOperatingAreaId(operatingAreaId);
      if (userId) {
        request.setUserId(userId);
      }
      return new Promise(async (resolve, reject) => {
        this.reservationServiceClient.getReservations(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
          if (!err) {
            const reservations = response.getGetReservation();
            if (response.hasError()) {
              reject(response.getError());
            } else if (response.hasGetReservation() && reservations) {
              resolve(reservations.getReservationsList().map(r => toReservation(r, operatingAreaTimezone)));
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
           } else {
              reject(err);
            }
          });
        });
    }

    public getPastReservations = (operatingAreaId: number, operatingAreaTimezone: string, userId?: number): Promise<PastReservation[]> => {
      const request = new GetReservationRequest();
      request.setOperatingAreaId(operatingAreaId);
      if (userId) {
        request.setUserId(userId);
      }
      return new Promise(async (resolve, reject) => {
        this.reservationServiceClient.getPastReservations(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
            if (!err) {
              const reservations = response.getGetPastReservation();
              if (response.hasError()) {
                reject(response.getError());
              } else if (response.hasGetPastReservation() && reservations) {
                resolve(reservations.getReservationsList().map(r => toPastReservation(r, operatingAreaTimezone)));
              } else {
                throw new Error(CONSOLE_ERROR_MSG);
              }
            } else {
              reject(err);
            }
          });
        });
    }

    public createReservations = (reservations: { operatingAreaId: number, timestamp: string, pickupLocationId: number, dropoffLocationId: number, seatCount: number, notes: string }[] ): Promise<number[]> => {
      const request = new CreateReservationsRequest();
      const transformedReservations = reservations.map(reservation => {
        const reservationRequest = new CreateReservationRequest();
        reservationRequest.setOperatingAreaId(reservation.operatingAreaId);
        reservationRequest.setReservationTimestamp(reservation.timestamp);
        reservationRequest.setPickupLocationId(reservation.pickupLocationId);
        reservationRequest.setDropoffLocationId(reservation.dropoffLocationId);
        reservationRequest.setNumberOfSeats(reservation.seatCount);
        reservationRequest.setNotes(reservation.notes);
        return reservationRequest;
      });
      request.setReservationsList(transformedReservations);

      return new Promise(async (resolve, reject) => {
        const stream = this.reservationServiceClient.createReservations(request, await requestMetadata(this.auth, this.oriApiKey)) as ClientReadableStream<CreateReservationOrErrorResponse>;
        const reservationIds: number[] = []

        stream.on('data', (response: CreateReservationOrErrorResponse) => {
          const reservation = response.getCreateReservation();
          if (response.hasError()) {
            reject(response.getError());
          } else if (response.hasCreateReservation() && reservation) {
            reservationIds.push(reservation.getReservationId());
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        });

        stream.on('status', status => {
          if (status.code === 0 || status.code === 14) {
            resolve(reservationIds);
          } else {
            reject();
          }
        });
      });
    }

    public updateReservation = (reservationId: number, timestamp: string, pickupLocationId: number, dropoffLocationId: number, numberOfSeats: number, notes: string): Promise<Empty | undefined> => {
      const request = new UpdateReservationRequest();
      request.setReservationId(reservationId);
      request.setReservationTimestamp(timestamp);
      request.setPickupLocationId(pickupLocationId);
      request.setDropoffLocationId(dropoffLocationId);
      request.setNumberOfSeats(numberOfSeats);
      request.setNotes(notes);

      return new Promise(async (resolve, reject) => {
        this.reservationServiceClient.updateReservation(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
          if (!err) {
            if (response.hasError()) {
              reject(response.getError());
            } else if (response.hasEmpty()){
              resolve(response.getEmpty());
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
          } else {
            reject(err);
          }
        });
      });
    }

    public cancelReservation = (reservationId: number): Promise<Empty | undefined> => {
      const request = new CancelReservationRequest();
      request.setReservationId(reservationId);

      return new Promise(async (resolve, reject) => {
        this.reservationServiceClient.cancelReservation(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
          if (!err) {
            if (response.hasError()) {
              reject(response.getError());
            } else if (response.hasEmpty()){
              resolve(response.getEmpty());
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
          } else {
            reject(err);
          }
          });
        });
    }

    public bulkCancelReservations = (operatingAreaId: number, date: string): Promise<Empty | undefined> => {
      const request = new BulkCancelReservationsRequest();
      request.setOperatingAreaId(operatingAreaId);
      request.setDate(date);

      return new Promise(async (resolve, reject) => {
        this.reservationServiceClient.bulkCancelReservations(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
          if (!err) {
            if (response.hasError()) {
              reject(response.getError());
            } else if (response.hasEmpty()){
              resolve(response.getEmpty());
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
          } else {
            reject(err);
          }
          });
        });
    }
  }
